// src/modules/clientes/view/client-gallery/components/GalleryPreviewModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal } from '../../../../components/Modal';
import { GalleryItem } from './gallery.types';
import { bytesToKb } from './gallery.utils';
import { useAppContext } from '../../../../contexts/AppContext';
import { Download, Trash2 } from 'lucide-react';

type Props = {
    preview: GalleryItem | null;
    onClose: () => void;
    onRemove: (id: string) => Promise<void>;
};

function downloadMedia(item: GalleryItem): boolean {
    try {
        const a = document.createElement('a');

        a.href = `${item.url}?download=1`;
        a.rel = 'noopener';
        a.download = item.name || 'midia-cliente';
        a.target = '_blank'; // iOS/Safari

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        return true;
    } catch (e) {
        console.error(e);
        return false;
    }
}

export function GalleryPreviewModal({ preview, onClose, onRemove }: Props) {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const [confirmingRemove, setConfirmingRemove] = useState(false);
    const [removing, setRemoving] = useState(false);

    useEffect(() => {
        setConfirmingRemove(false);
    }, [preview?.id]);

    if (!preview) return null;

    const handleRemove = async () => {
        try {
            setRemoving(true);
            await onRemove(preview.id);
            successSnackbar('Mídia removida.');
            onClose();
        } catch (e) {
            console.error(e);
            errorSnackbar('Não foi possível remover a mídia.');
        } finally {
            setRemoving(false);
        }
    };

    return (
        <Modal
            onClose={onClose}
            title={preview.type === 'image' ? 'Foto do cliente' : 'Vídeo do cliente'}
            subTitle={`${bytesToKb(preview.size)} KB • ${new Date(preview.createdAt).toLocaleString('pt-BR')}`}
        >
            <div className="space-y-4">
                {preview.type === 'image' ? (
                    <img
                        src={preview.url}
                        alt={preview.name}
                        className="max-h-[65vh] w-full rounded-[var(--sf-radius-md)] object-contain"
                    />
                ) : (
                    <video
                        src={preview.url}
                        controls
                        className="max-h-[65vh] w-full rounded-[var(--sf-radius-md)]"
                    />
                )}

                {confirmingRemove ? (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <span className="mr-auto text-sm text-base-content/70">Remover esta mídia da galeria?</span>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setConfirmingRemove(false)}
                            disabled={removing}
                        >
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-error btn-sm" onClick={handleRemove} disabled={removing}>
                            {removing && <span className="loading loading-spinner loading-xs"></span>}
                            Remover
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center justify-between gap-2">
                        <button
                            type="button"
                            className="sf-link"
                            onClick={() => {
                                if (downloadMedia(preview)) {
                                    successSnackbar('Download iniciado.');
                                } else {
                                    errorSnackbar('Não foi possível baixar a mídia.');
                                }
                            }}
                        >
                            <Download aria-hidden />
                            Baixar
                        </button>

                        <button
                            type="button"
                            className="sf-link !text-error"
                            onClick={() => setConfirmingRemove(true)}
                        >
                            <Trash2 aria-hidden />
                            Remover
                        </button>
                    </div>
                )}
            </div>
        </Modal>
    );
}
